import { request, unwrap } from '../client';
import type { ApiResponse, PlatformDefaultsDto } from '../types';

/**
 * Spec 0037 — read-only platform-defaults (`GET /api/saas-admin/platform-defaults`).
 * Display-only fallback values, config-sourced; changes only on backend redeploy, so
 * we cache the result for the whole session (one fetch shared by all consumers).
 *
 * Feature-detected: until saas-auth 0038 ships the endpoint 404s — any error resolves
 * to `null` so callers fall back to today's static text / behavior.
 */
let cache: PlatformDefaultsDto | null | undefined; // undefined = not fetched yet
let inflight: Promise<PlatformDefaultsDto | null> | null = null;

export const platformDefaultsApi = {
  async get(): Promise<PlatformDefaultsDto | null> {
    if (cache !== undefined) return cache;
    if (inflight) return inflight;
    inflight = request<ApiResponse<PlatformDefaultsDto>>('GET', '/api/saas-admin/platform-defaults')
      .then((res) => {
        cache = unwrap(res);
        return cache;
      })
      .catch(() => {
        cache = null; // 404 (pre-ship) or any error → feature-detect fallback
        return null;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  },

  /** Test-only: clear the session cache between cases. */
  _reset() {
    cache = undefined;
    inflight = null;
  },
};
